import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useSeo } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-book-open-line",
    title: "资料优先",
    desc: "以片目、人物与时间为三条主线整理内容，条目之间互相链接，避免信息孤岛。",
  },
  {
    icon: "ri-eye-line",
    title: "舒适阅读",
    desc: "控制正文密度与行距，减少干扰元素，让长文阅读保持稳定的节奏。",
  },
  {
    icon: "ri-time-line",
    title: "脉络清晰",
    desc: "每一部影片都放回系列的年代语境中，帮助读者理解风格与制作条件的变化。",
  },
];

const coverage = [
  { icon: "ri-film-line", title: "全系列片目", desc: "收录系列电影与纪念作品，包含上映年份、片长、地区与类型标签。" },
  { icon: "ri-file-text-line", title: "剧情与看点", desc: "以分段方式整理剧情梗概，并标注每部作品的叙事特色。" },
  { icon: "ri-user-3-line", title: "演职员档案", desc: "整理主要演员的简介与参演作品，支持从影片直达演员资料。" },
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "汇总重映、修复、新作动态与类型研究文章，按新闻、专题、评论分类。" },
];

export default function About() {
  useSeo({
    title: "关于我们 - 蜜桃成熟时电影",
    description:
      "了解蜜桃成熟时电影资料站的定位、内容范围与联系方式。本站专注系列电影的剧情介绍、演员资料与影视资讯整理。",
    keywords: "关于我们,蜜桃成熟时电影,影视资料站,网站介绍,联系方式",
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            关于蜜桃成熟时电影
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
            一个专注系列电影的资料型站点。我们相信，好的影视内容页面不需要喧哗，只需要把信息放在该在的位置。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-4 py-10 md:px-6 md:py-14">
        <section className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
          <div>
            <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-accent-600">
              <span className="h-[2px] w-6 bg-accent-500" />
              Our Positioning
            </p>
            <h2 className="mt-3 font-heading text-2xl font-semibold leading-snug text-foreground-950 md:text-3xl">
              把一段银幕记忆，整理成可以反复查阅的资料
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-[1.95] text-foreground-700">
              <p>
                网站围绕「蜜桃成熟时」系列展开，面向喜爱该系列的影迷、电影爱好者、影视行业从业者与研究者。我们不追逐即时热点，而是希望把零散的信息整理成结构清晰、可以长期引用的条目。
              </p>
              <p>
                内容以片目为核心，向外延伸至剧情梗概、演职员资料与相关资讯。每一条目都尽量标注年份、导演、类型等基础信息，方便读者快速判断与自己需求的相关性。
              </p>
              <p>
                本站为纯前端演示项目，所有影片、演员与资讯内容均为模拟数据，用于展示影视资料站的浏览与检索体验，不涉及任何真实业务接口。
              </p>
            </div>
          </div>
          <div className="h-[260px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[360px]">
            <img
              src="https://readdy.ai/api/search-image?query=warm%20film%20archive%20interior%20with%20wooden%20shelves%20of%20labeled%20film%20canisters%20and%20books%2C%20a%20reading%20desk%20with%20a%20small%20lamp%2C%20soft%20afternoon%20sunlight%20through%20sheer%20curtains%2C%20cream%20and%20peach%20color%20palette%2C%20quiet%20editorial%20interior%20photography&width=1200&height=900&seq=about-archive-shelf&orientation=landscape"
              alt="影视资料整理与阅读空间"
              title="关于蜜桃成熟时电影资料站"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </section>

        <section className="mt-14 md:mt-20">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">我们坚持的三件事</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {principles.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-accent-700">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 rounded-lg bg-background-100 p-6 md:mt-20 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-12">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-accent-600">
                <span className="h-[2px] w-6 bg-accent-500" />
                What We Cover
              </p>
              <h2 className="mt-3 font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-2xl">
                内容范围
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                从一部影片出发，逐步补齐人物、年代与延伸讨论。所有条目都可以通过顶部搜索或分类筛选快速找到。
              </p>
              <Link
                to="/category"
                className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                开始浏览片目
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {coverage.map((item) => (
                <div key={item.title} className="rounded-lg bg-background-50 p-4">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${item.icon} text-sm`} />
                    </span>
                    <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-14 md:mt-20">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你发现条目信息有误，或希望补充影片资料、提供采访线索，欢迎通过邮件与我们联系。我们会在收到后尽快核对。
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <a
                  href="mailto:hello@peachcinema.example"
                  className="group flex items-center justify-between gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-accent-700">
                      <i className="ri-mail-line" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">邮件反馈</span>
                      <span className="block text-xs text-foreground-500">hello@peachcinema.example</span>
                    </span>
                  </span>
                  <i className="ri-arrow-right-line text-foreground-300 transition-colors group-hover:text-accent-600" />
                </a>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://911fuli2.asia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-accent-600"
                  >
                    <i className="ri-weibo-line" />
                    微博
                  </a>
                  <a
                    href="https://www.douban.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-accent-600"
                  >
                    <i className="ri-douban-line" />
                    豆瓣
                  </a>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-accent-600"
                  >
                    <i className="ri-newspaper-line" />
                    资讯更新
                  </Link>
                </div>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
                注：以上邮箱与社交账号仅用于演示展示，不接收真实投稿。
              </p>
            </div>

            <div>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                版权与使用须知
              </h2>
              <div className="mt-4 space-y-4 rounded-lg bg-background-100 p-5 text-xs leading-relaxed text-foreground-600">
                <p>
                  <strong className="font-medium text-foreground-900">内容性质：</strong>
                  本站为影视资料演示站点，全部影片条目、剧情文本、演员简介与资讯文章均为模拟内容，不对应任何真实发行信息。
                </p>
                <p>
                  <strong className="font-medium text-foreground-900">使用限制：</strong>
                  站内不提供在线播放、下载、票务或任何形式的交易服务。页面中的按钮与链接仅用于演示前端交互。
                </p>
                <p>
                  <strong className="font-medium text-foreground-900">引用说明：</strong>
                  如需引用站内文字，请注明来源为本站演示内容。图片为生成式示意图，仅用于版式演示。
                </p>
                <p>
                  <strong className="font-medium text-foreground-900">内容反馈：</strong>
                  如对信息结构、阅读体验有改进建议，可通过上方邮件入口反馈。
                </p>
              </div>
              <p className="mt-4 text-[11px] text-foreground-400">
                © 2026 蜜桃成熟时电影 · Peach Cinema Archive
              </p>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}